import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { AlertTriangle, Loader2 } from 'lucide-react';

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export interface LoadingBadgeProps {
  /** Display name shown in the badge header. */
  name: string;
  /** Current phase of the loading lifecycle. */
  phase: 'fetching' | 'decoding' | 'failed';
  /** 0..1 progress fraction (only meaningful during `fetching` with known total). */
  progress?: number;
  /** Bytes received so far. */
  loadedBytes?: number;
  /** Total expected bytes (from Content-Length or equivalent). */
  totalBytes?: number;
  /** Error message shown when `phase === 'failed'`. */
  error?: string;
  /** Fallback status labels. Provide translations for each phase. */
  labels?: {
    fetching?: string;
    decoding?: string;
    failed?: string;
  };
  /** Additional class names for the outer wrapper. */
  className?: string;
}

/**
 * A reusable loading badge that shows fetch/decode progress for a single
 * item. Renders a determinate progress bar when a percentage is available,
 * an indeterminate pulsing bar otherwise, and an error state with a
 * warning icon on failure.
 */
export const LoadingBadge = ({
  name,
  phase,
  progress,
  loadedBytes,
  totalBytes,
  error,
  labels,
  className,
}: LoadingBadgeProps) => {
  const failed = phase === 'failed';
  const decoding = phase === 'decoding';
  const fetching = phase === 'fetching';
  const pct = typeof progress === 'number' ? Math.round(progress * 100) : null;

  const showDeterminate = fetching && pct !== null;
  const showIndeterminate = !failed && !showDeterminate;

  return (
    <div
      className={cn(
        'min-w-[280px] max-w-[420px] rounded-md border bg-white/90 dark:bg-stone-900/90 backdrop-blur-md',
        'px-3 py-2 shadow-md text-xs',
        failed ? 'border-red-300 dark:border-red-900/60' : 'border-slate-200 dark:border-stone-700',
        className,
      )}>
      <div className='flex items-center gap-2'>
        {failed ? (
          <AlertTriangle className='size-3.5 shrink-0 text-red-500' />
        ) : (
          <Loader2 className='size-3.5 shrink-0 animate-spin text-blue-500' />
        )}
        <span className='flex-1 truncate font-medium'>{name}</span>
        {fetching && pct !== null && <span className='tabular-nums text-slate-500'>{pct}%</span>}
      </div>

      <div className='mt-1.5 text-[11px] text-slate-500 dark:text-slate-400'>
        {failed
          ? (error ?? labels?.failed ?? 'Failed')
          : decoding
            ? (labels?.decoding ?? 'Decoding…')
            : totalBytes
              ? `${formatBytes(loadedBytes ?? 0)} / ${formatBytes(totalBytes)}`
              : loadedBytes
                ? formatBytes(loadedBytes)
                : (labels?.fetching ?? 'Loading…')}
      </div>

      {showDeterminate && <Progress value={pct ?? 0} className='mt-1.5 h-1' />}
      {showIndeterminate && (
        <div className='mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-stone-800'>
          <div className='h-full w-1/3 animate-[loadingpulse_1.4s_ease-in-out_infinite] rounded-full bg-blue-500' />
        </div>
      )}
    </div>
  );
};

/** Inject the keyframe animation used by the indeterminate bar. */
export const LoadingBadgeKeyframes = () => (
  <style>{`@keyframes loadingpulse { 0%{transform:translateX(-100%)} 100%{transform:translateX(300%)} }`}</style>
);
